import React, { useState } from 'react';
import { 
  ThemeProvider, 
  createTheme, 
  CssBaseline,
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import Dropzone from './components/Dropzone';
import ImagePreview from './components/ImagePreview';
import FileQueue, { QueuedFile } from './components/FileQueue';
import Header from './components/Header';
import { API_BASE_URL } from './config';
import axios from 'axios';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
}));

const ContentBox = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

const PreviewContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: theme.spacing(3),
  marginTop: theme.spacing(3),
}));

function App() {
  const [queuedFiles, setQueuedFiles] = useState<QueuedFile[]>([]);
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [translatedImage, setTranslatedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleImageDrop = async (files: File[]) => {
    if (files.length === 0) return;
    
    // Add new files to the queue
    const newQueuedFiles: QueuedFile[] = files.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      file,
      status: 'pending',
      progress: 0
    }));
    
    setQueuedFiles(prev => [...prev, ...newQueuedFiles]);
  };

  const removeFile = (id: string) => {
    setQueuedFiles(prev => prev.filter(file => file.id !== id));
  };

  const clearAllFiles = () => {
    setQueuedFiles([]);
    setOriginalImage(null);
    setTranslatedImage(null);
  };

  const processQueue = async () => {
    if (queuedFiles.length === 0 || isProcessing) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      // Process files one by one
      for (let i = 0; i < queuedFiles.length; i++) {
        const queuedFile = queuedFiles[i];
        
        // Skip if already processed
        if (queuedFile.status === 'completed' || queuedFile.status === 'error') {
          continue;
        }
        
        // Update status to processing
        setQueuedFiles(prev => prev.map(file => 
          file.id === queuedFile.id 
            ? { ...file, status: 'processing', progress: 0 }
            : file
        ));
        
        try {
          // Simulate progress updates
          const progressInterval = setInterval(() => {
            setQueuedFiles(prev => prev.map(file => 
              file.id === queuedFile.id 
                ? { ...file, progress: Math.min(file.progress + 10, 90) }
                : file
            ));
          }, 500);
          
          // Convert file to base64
          const base64Image = await fileToBase64(queuedFile.file);
          
          // Call the translation API using config
          const response = await axios.post(`${API_BASE_URL}/api/translate`, {
            image: base64Image
          });
          
          clearInterval(progressInterval);
          
          // Update status to completed
          setQueuedFiles(prev => prev.map(file => 
            file.id === queuedFile.id 
              ? { 
                  ...file, 
                  status: 'completed', 
                  progress: 100,
                  result: response.data.translatedImage
                }
              : file
          ));
          
          // Set the last completed image for preview
          setTranslatedImage(response.data.translatedImage);
          
        } catch (err) {
          console.error('Translation error:', err);
          
          // Update status to error
          setQueuedFiles(prev => prev.map(file => 
            file.id === queuedFile.id 
              ? { 
                  ...file, 
                  status: 'error', 
                  progress: 0,
                  error: 'Failed to translate image'
                }
              : file
          ));
        }
        
        // Small delay between files
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
    } catch (err) {
      setError('Failed to process queue. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const pendingFiles = queuedFiles.filter(f => f.status === 'pending');
  const hasFilesToProcess = pendingFiles.length > 0 && !isProcessing;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <StyledContainer maxWidth="lg">
        <Header />
        
        <ContentBox>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography variant="h1" gutterBottom align="center">
              Webtoon Translator
            </Typography>
            <Typography variant="h5" color="text.secondary" align="center" paragraph>
              Upload multiple webtoon panels and get instant translations powered by OpenAI GPT-3.5-turbo
            </Typography>
          </motion.div>

          <Paper 
            elevation={3}
            sx={{ 
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3
            }}
          >
            <Dropzone onDrop={handleImageDrop} disabled={isProcessing} />
            
            {hasFilesToProcess && (
              <Button
                variant="contained"
                size="large"
                onClick={processQueue}
                disabled={isProcessing}
                sx={{ minWidth: 200 }}
              >
                Process {pendingFiles.length} File{pendingFiles.length !== 1 ? 's' : ''}
              </Button>
            )}
            
            {isProcessing && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} />
                <Typography>Processing your webtoon panels...</Typography>
              </Box>
            )}
          </Paper>

          {/* File Queue */}
          <FileQueue
            files={queuedFiles}
            onRemoveFile={removeFile}
            onClearAll={clearAllFiles}
          />

          {/* Image Previews */}
          {queuedFiles.some(f => f.status === 'completed') && (
            <PreviewContainer>
              {queuedFiles
                .filter(f => f.status === 'completed' && f.result)
                .map((queuedFile) => (
                  <ImagePreview 
                    key={queuedFile.id}
                    image={queuedFile.result!}
                    title={`Translated: ${queuedFile.file.name}`}
                  />
                ))}
            </PreviewContainer>
          )}
        </ContentBox>

        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={() => setError(null)}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>
      </StyledContainer>
    </ThemeProvider>
  );
}

export default App;
